import { defineStore } from 'pinia';
import { useNuxtApp } from 'nuxt/app';
import { computed, readonly, ref } from 'vue';
import { SEO_MODULE_BASKET, SEO_MODULE_CAREER_JOB, SEO_MODULE_CAREER_NEWS, SEO_MODULE_CATALOG, SEO_MODULE_CATEGORY, SEO_MODULE_CMS, SEO_MODULE_COMPARE, SEO_MODULE_CSR, SEO_MODULE_FAQ, SEO_MODULE_FOOD_DELIVERY_MAP, SEO_MODULE_GIFT_CARD_REQUEST, SEO_MODULE_GLOBAL, SEO_MODULE_INSPIRATIONS, SEO_MODULE_LOYALTY_FAQ, SEO_MODULE_NEWS, SEO_MODULE_PETROL, SEO_MODULE_PETROL_FAQ, SEO_MODULE_PRESS, SEO_MODULE_PRIZE_GAME, SEO_MODULE_PRODUCT, SEO_MODULE_SDS, SEO_MODULE_SHORTLIST, SEO_MODULE_STORE, SEO_MODULE_UNSUBSCRIBE } from '~~/common/config/SeoConfig';
import { AW_AX_CACHE_GLOBAL } from '~~/common/plugins/axios/awAxCache';
import { SeoTitle } from '~~/common/utils/SeoTitle';
import type { Schema } from '~~/common/types/api';

// Maps each SEO module symbol to the backend resource path segment. Module-level so the
// SeoModule union can be derived from its keys.
const seoModuleStrings = {
  [SEO_MODULE_GLOBAL]: 'global',
  [SEO_MODULE_PRODUCT]: 'productSeoResource',
  [SEO_MODULE_CATEGORY]: 'categorySeoResource',
  [SEO_MODULE_CMS]: 'cms_content',
  [SEO_MODULE_SDS]: 'productSdsDocumentsSeoResource',
  [SEO_MODULE_CATALOG]: 'catalogSeoResource',
  [SEO_MODULE_GIFT_CARD_REQUEST]: 'giftCardRequestSeoResource',
  [SEO_MODULE_CSR]: 'csrSeoResource',
  [SEO_MODULE_FAQ]: 'faqSeoResource',
  [SEO_MODULE_FOOD_DELIVERY_MAP]: 'foodDeliveryMap',
  [SEO_MODULE_INSPIRATIONS]: 'inspirationsSeoResource',
  [SEO_MODULE_NEWS]: 'newsSeoResource',
  [SEO_MODULE_PRESS]: 'pressSeoResource',
  [SEO_MODULE_PRIZE_GAME]: 'prizeGameSeoResource',
  [SEO_MODULE_UNSUBSCRIBE]: 'unsubscribePage',
  [SEO_MODULE_LOYALTY_FAQ]: 'loyaltyFaqSeoResource',
  [SEO_MODULE_PETROL_FAQ]: 'petrolFaqSeoResource',
  [SEO_MODULE_COMPARE]: 'compareSeoResource',
  [SEO_MODULE_CAREER_NEWS]: 'careerNewsSeoResource',
  [SEO_MODULE_CAREER_JOB]: 'careerJobsSeoResource',
  [SEO_MODULE_BASKET]: 'basketSeoResource',
  [SEO_MODULE_STORE]: 'storesSeoResource',
  [SEO_MODULE_PETROL]: 'petrolSeoResource',
  [SEO_MODULE_SHORTLIST]: 'shortListSeoResource',
} as const;

export type SeoModule = keyof typeof seoModuleStrings;

type SeoData = Partial<Schema<'SeoModel'>>;

interface SeoDataPayload {
  seoModule: SeoModule;
  id?: number | string;
}

interface SeoRequestEnRoute {
  seoModule: string;
  id?: number | string;
}

interface SeoMetaTag {
  hid: string;
  name: string;
  content: string;
}

interface SeoHead {
  title: string;
  __dangerouslyDisableSanitizers: string[];
  meta: SeoMetaTag[];
}

export const useSeoStore = defineStore('seo', () => {
  const nuxtApp = useNuxtApp();
  // TODO: Something feels fishy. `_seoRequestsEnRoute` seems to be incorrectly set with not promise data, while it is handled as a promise array in other places.
  const _seoRequestsEnRoute = ref<SeoRequestEnRoute[]>([]);
  const _data = ref<Partial<SeoHead>>({});
  const _titlePartPrefix = ref('');
  const _titlePartMain = ref('');
  const _titlePartPostfix = ref('');
  const _responseCache = ref<Record<string, SeoData | undefined>>({});
  const _promiseCache = ref<Record<string, Promise<SeoData | undefined>>>({});

  function removeSeoRequestsEnRoute (payload: unknown) {

    const i = _seoRequestsEnRoute.value.findIndex((req) => req === payload);
    if (i !== -1) {
      _seoRequestsEnRoute.value.splice(i, 1);
    }
  }

  function createSeoTitleFromStore () {
    return new SeoTitle(
      _titlePartPrefix.value,
      _titlePartMain.value,
      _titlePartPostfix.value,
    );
  }

  function _setSeoData (payload?: SeoData) {
    _titlePartPrefix.value = '';
    _titlePartMain.value = typeof payload?.title === 'string' ? payload.title : '';
    _titlePartPostfix.value = typeof payload?.titlePostfix === 'string' ? payload.titlePostfix : '';
    _data.value = _computeSeoHeads({
      computedTitle: createSeoTitleFromStore().joinedTitle(),
      seo: payload || {},
    });
  }

  async function getCareerPageTitle (
    payload?: {
      pageTitle?: string;
    },
  ) {
    const { $awt } = nuxtApp;
    return new SeoTitle(
      payload?.pageTitle || '',
      `${payload?.pageTitle ? ' - ' : ''}${$awt('aw.career.seo.title')}`,
      (await getSeoData({ seoModule: SEO_MODULE_GLOBAL, id: -1 }))?.titlePostfix,
    ).joinedTitle();
  }

  async function fetchSeoData (payload: SeoDataPayload) {
    const { $logger } = nuxtApp;
    let seoReq;
    try {
      seoReq = getSeoData(payload);
      _seoRequestsEnRoute.value.push({ ...payload, seoModule: getSeoModuleString(payload.seoModule) });
      _setSeoData({ title: 'Auchan', ...await seoReq });
    } catch (error) {
      _setSeoData({ title: 'Auchan' });
      $logger.error(error);
    } finally {
      removeSeoRequestsEnRoute(seoReq);
    }
  }

  function getSeoModuleString (seoModule: SeoModule) {
    return seoModuleStrings[seoModule];
  }

  async function getSeoData ({
    seoModule,
    id,
  }: SeoDataPayload = {
    seoModule: SEO_MODULE_GLOBAL,
    id: -1,
  }): Promise<SeoData | undefined> {
    const { $logger, $api, $i18n } = nuxtApp;
    try {
      const seoModuleString = getSeoModuleString(seoModule);
      if (!seoModuleString) {
        $logger.error('Missing seo module');
      }
      const cacheKey = `${seoModuleString}__${id}__${$i18n.locale.value}`;
      if (_responseCache.value[cacheKey]) {
        /* pass */
      } else if (_promiseCache.value[cacheKey]) {
        await _promiseCache.value[cacheKey];
      } else {
        const seoRequest: Promise<SeoData | undefined> = ($api.$get<SeoData>(`/seo/${seoModuleString}${id ? `/${id}` : ''}`, { awAxCache: AW_AX_CACHE_GLOBAL })
          .catch((err) => {
            $logger.error(err);
            // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
            delete _promiseCache.value[cacheKey];
            return undefined;
          })
        );
        _promiseCache.value[cacheKey] = seoRequest;
        _responseCache.value[cacheKey] = await seoRequest;
      }
      return _responseCache.value[cacheKey];
    } catch (err) {
      $logger.error(err);
    }
    return {};
  }

  function _computeSeoHeads ({
    computedTitle,
    seo,
  }: {
    computedTitle: string;
    seo: SeoData;
  }): SeoHead {
    const { $awMergedConfig, $logger } = nuxtApp;
    const {
      description,
      keywords,
      ogTitle,
      ogDescription,
      ogType,
      ogImage,
      noIndex,
    } = {
      description: seo.description,
      keywords: seo.keywords,
      ogTitle: seo.ogTitle,
      ogDescription: seo.ogDescription,
      ogType: seo.ogType,
      ogImage: seo.ogImage,
      noIndex: seo.noIndex,
    };
    return {
      title: `${computedTitle}`,
      __dangerouslyDisableSanitizers: ['script'],
      meta: [...(function* (): Generator<SeoMetaTag> {
        try {
          yield {
            hid: 'build_identifier',
            name: 'build_identifier',
            content: __AW_BUILD_IDENTIFIER__,
          };
          if (description) {
            yield {
              hid: 'description',
              name: 'description',
              content: description,
            };
          }
          if (keywords) {
            yield {
              hid: 'keywords',
              name: 'keywords',
              content: keywords,
            };
          }
          if (ogTitle) {
            yield {
              hid: 'og:title',
              name: 'og:title',
              content: ogTitle,
            };
          }
          if (ogDescription) {
            yield {
              hid: 'og:description',
              name: 'og:description',
              content: ogDescription,
            };
          }
          if (ogType) {
            yield {
              hid: 'og:type',
              name: 'og:type',
              content: ogType,
            };
          }
          if (ogImage) {
            yield {
              hid: 'og:image',
              name: 'og:image',
              content: ogImage,
            };
            yield {
              hid: 'og:image:type',
              name: 'og:image:type',
              content: guessImgMime(ogImage) || 'image/png',
            };
          }
          if (noIndex) {
            yield {
              hid: 'robots',
              name: 'robots',
              content: 'noindex,nofollow',
            };
          }
          if ($awMergedConfig.facebookDomainVerificationId) {
            yield {
              hid: 'facebook-domain-verification',
              name: 'facebook-domain-verification',
              content: $awMergedConfig.facebookDomainVerificationId,
            };
          }
        } catch (err) {
          $logger.error(err);
        }
      })()],
    };
  }

  return {
    _responseCache,
    _seoRequestsEnRoute,
    _data,
    _titlePartPrefix,
    _titlePartMain,
    _titlePartPostfix,
    getSeoReadonlyRequestsEnRoute: () => readonly(_seoRequestsEnRoute),
    getSeoReadonlyData: () => readonly(_data),
    getSeoReadonlyTitleParts: () => readonly(computed(createSeoTitleFromStore)),
    fetchSeoData,
    getSeoData,
    getCareerPageTitle,
  };
});

function guessImgMime (url: string) {
  const splitted = url.split('.');
  if (splitted.length > 1) {
    return `image/${splitted.pop()!.replace(/jpg$/, 'jpeg')}`;

  } else {
    return '';
  }
}
